import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatTableModule } from '@angular/material/table';
import { ReportsService } from '../../../core/services/reports/reports.service';
import { AlertService } from '../../../core/services/alert/alert.service';
import { AccountsService } from '../../../core/services/accounts/accounts.service';
import { copyToClipboard as copyToClipboardUtil } from '../../../core/utils/clipboard.utils';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-reports-not-delivered',
  standalone: true,
  imports: [CommonModule, RouterLink, TranslateModule, MatTableModule],
  templateUrl: './reports-not-delivered.component.html',
  styleUrl: './reports-not-delivered.component.css',
})
export class ReportsNotDeliveredComponent implements OnInit {
  tokens: any[] = [];
  isLoading = true;
  displayedColumns: string[] = ['supplier', 'orders_count', 'without_date_count', 'expires_at', 'status', 'actions'];

  constructor(
    private reportsService: ReportsService,
    private alertService: AlertService,
    private accountsService: AccountsService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadTokens();
  }

  loadTokens(): void {
    this.isLoading = true;
    this.reportsService.getNotDeliveredTokens().subscribe({
      next: (response: any) => {
        this.tokens = (response.data || []).filter((t: any) => t.not_delivered_orders_count > 0);
        this.isLoading = false;
      },
      error: (err: any) => {
        this.isLoading = false;
        this.tokens = [];
        this.alertService.error(err.error || 'swal_messages.error_occurred');
      },
    });
  }

  openSupplierOrders(token: any): void {
    const supplierRef = token.supplier?.reference;
    if (!supplierRef) return;
    window.open(`/orders/not-delivered?supplier=${supplierRef}`, '_blank');
  }

  /**
   * Un solo botón: si el token está activo → copia el link.
   * Si está expirado → regenera, recarga la tabla y copia el nuevo link.
   */
  copyOrRefreshAndCopy(token: any, event: Event): void {
    if (!token.is_expired && token.link) {
      // Activo: solo copiar
      this.doCopy(token.link, event);
    } else {
      // Expirado: regenerar y copiar
      this.alertService.loading();
      this.reportsService
        .refreshNotDeliveredToken(token.supplier.reference)
        .subscribe({
          next: (res: any) => {
            this.alertService.close();
            const newLink = res.data?.token
              ? window.location.origin + '/public/orders/' + res.data.token + '/supplier-update'
              : null;
            if (newLink) {
              navigator.clipboard.writeText(newLink);
            }
            this.alertService.success('swal_messages.orders.bulk_update_token_refreshed');
            this.doCopy(newLink || '', event);
            this.loadTokens();
          },
          error: (err: any) => {
            this.alertService.close();
            this.alertService.error(err.error || 'swal_messages.error_occurred');
          },
        });
    }
  }

  private doCopy(link: string, event: Event): void {
    if (!link) return;
    navigator.clipboard.writeText(link);
    const button = (event.currentTarget || event.target) as HTMLElement;
    button.classList.add('copied');
    setTimeout(() => button.classList.remove('copied'), 1000);
  }

  async openCreateModal(): Promise<void> {
    this.accountsService.getData().subscribe({
      next: async (response: any) => {
        const accounts = response.data || [];
        const suppliers = accounts.filter(
          (a: any) => a.type === 'supplier'
        );

        if (suppliers.length === 0) {
          this.alertService.warning('reports.no_suppliers_available');
          return;
        }

        const inputOptions: Record<string, string> = {};
        suppliers.forEach((s: any) => {
          inputOptions[s.reference] = s.name;
        });

        const title = this.translate.instant('reports.generate_token');
        const selectLabel = this.translate.instant('reports.select_supplier');
        const confirmText = this.translate.instant('swal_messages.confirm');
        const cancelText = this.translate.instant('swal_messages.cancel');

        const result = await Swal.fire({
          title: title,
          input: 'select',
          inputOptions: inputOptions,
          inputPlaceholder: selectLabel,
          showCancelButton: true,
          confirmButtonText: confirmText,
          cancelButtonText: cancelText,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          inputValidator: (value) => {
            if (!value) {
              return this.translate.instant('reports.select_supplier');
            }
            return null;
          },
        });

        if (!result.isConfirmed || !result.value) return;

        this.alertService.loading();
        this.reportsService
          .createNotDeliveredToken(result.value)
          .subscribe({
            next: (res: any) => {
              this.alertService.close();
              const link =
                window.location.origin +
                '/public/orders/' +
                res.data.token +
                '/supplier-update';
              Swal.fire({
                title: this.translate.instant(
                  'swal_messages.orders.bulk_update_token_generated'
                ),
                html: `<div style="word-break:break-all;padding:10px;background:#f5f5f5;border-radius:6px;font-size:13px;margin-top:8px;">${link}</div>`,
                icon: 'success',
                confirmButtonText: this.translate.instant(
                  'reports.copy_link'
                ),
                confirmButtonColor: '#3085d6',
              }).then((r) => {
                if (r.isConfirmed) {
                  navigator.clipboard.writeText(link);
                }
              });
              this.loadTokens();
            },
            error: (err: any) => {
              this.alertService.close();
              if (err.code === 409) {
                this.handleTokenAlreadyExists(result.value);
              } else {
                this.alertService.error(
                  err.error || 'swal_messages.error_occurred'
                );
              }
            },
          });
      },
      error: () => {
        this.alertService.error('swal_messages.error_occurred');
      },
    });
  }

    copyToClipboard(value: string, event: Event, lowercase = false): void {
      copyToClipboardUtil(value, event, lowercase);
    }

  private async handleTokenAlreadyExists(
    supplierReference: string
  ): Promise<void> {
    const result = await this.alertService.confirmation(
      'reports.token_already_exists_refresh',
      'reports.token_already_exists'
    );
    if (!result.isConfirmed) return;

    this.alertService.loading();
    this.reportsService
      .refreshNotDeliveredToken(supplierReference)
      .subscribe({
        next: () => {
          this.alertService.close();
          this.alertService.success(
            'swal_messages.orders.bulk_update_token_refreshed'
          );
          this.loadTokens();
        },
        error: (err: any) => {
          this.alertService.close();
          this.alertService.error(
            err.error || 'swal_messages.error_occurred'
          );
        },
      });
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yy} ${hh}:${min}`;
  }
}
